import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, TableCell, TableRow } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import HearingIcon from "@material-ui/icons/Hearing";
import MicIcon from "@material-ui/icons/Mic";
import NoteIcon from "@material-ui/icons/Note";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import ListeningDialog from "../../listenings/ListeningDialog";
import { buildAveragedListeningOwner } from "../../listenings/listeningOwners";
import CalibrationNotesDialog from "../../notes/CalibrationNotesDialog";
import useCalibrationNotes from "../../notes/useCalibrationNotes";
import ActionCountCell from "../../shared/ActionCountCell";
import AveragedExpandedRowContent from "./AveragedExpandedRowContent";
import { formatAppVolume } from "../../shared/appVolumeUtils";
import {
  formatMicrophoneSignalPower,
  getMicrophoneSignalPower,
  resolveMicrophoneCalibration,
} from "../../shared/microphoneCalibrationUtils";
import {
  addAveragedNote,
  addRawNote,
  deleteNote,
  deleteRawNote,
  excludeCheckBox,
  getAveragedNotes,
  getRawNotes,
  includeCheckBox,
  updateNote,
  updateRawNote,
} from "../../utils/NetworkHandler";

export default function ConfigurableRow({
  data,
  config,
  removeGroupHandler,
  mainCalibrations,
  selectedOption,
  updateData,
  isDetailedData,
}) {
  const [open, setOpen] = useState(false);
  const [checkBoxes, setCheckBoxes] = useState({});
  const [selectedListeningOwner, setSelectedListeningOwner] = useState(null);
  const [listeningDialogOpen, setListeningDialogOpen] = useState(false);
  const [listeningCountState, setListeningCountState] = useState(
    data?.listeningCount != null ? data.listeningCount : 0,
  );

  const isModelView = Number(selectedOption) === 3;
  const displayedFrequencies =
    isDetailedData && isModelView
      ? [500, 1000, 2000, 4000, 8000, 16000]
      : [500, 1000, 2000, 4000];

  useEffect(() => {
    setListeningCountState(
      data?.listeningCount != null ? data.listeningCount : 0,
    );
  }, [data?.listeningCount]);

  useEffect(() => {
    const initialState = (data?.components || []).reduce((acc, component) => {
      const componentId = component?.deviceCalibration?.id;
      if (componentId) {
        acc[componentId] = component.isIncluded ?? false;
      }
      return acc;
    }, {});

    setCheckBoxes(initialState);
  }, [data?.components]);

  useEffect(() => {
    setOpen(false);
  }, [selectedOption]);

  const averagedInitialCountsByOwnerId = useMemo(
    () => ({
      [data?.id]: data?.notes?.size != null ? data.notes.size : 0,
    }),
    [data?.id, data?.notes?.size],
  );

  const rawInitialCountsByOwnerId = useMemo(() => {
    const nextCounts = {};

    (data?.components || []).forEach((component) => {
      const calibration = component?.deviceCalibration;
      if (calibration?.id) {
        nextCounts[calibration.id] = calibration?.notesCount ?? 0;
      }
    });

    return nextCounts;
  }, [data?.components]);

  const averagedNotes = useCalibrationNotes({
    getOwnerId: (owner) => owner?.id,
    getNotes: getAveragedNotes,
    addNote: addAveragedNote,
    updateNote,
    deleteNote,
    initialCountsByOwnerId: averagedInitialCountsByOwnerId,
    onNotesChanged: async () => {
      if (updateData) {
        await updateData();
      }
    },
  });

  const rawNotes = useCalibrationNotes({
    getOwnerId: (owner) => owner?.id,
    getNotes: getRawNotes,
    addNote: addRawNote,
    updateNote: updateRawNote,
    deleteNote: deleteRawNote,
    initialCountsByOwnerId: rawInitialCountsByOwnerId,
    onNotesChanged: async () => {
      if (updateData) {
        await updateData();
      }
    },
  });

  const topLevelListeningOwner = useMemo(
    () => (isModelView ? buildAveragedListeningOwner(data) : null),
    [
      isModelView,
      data?.id,
      data?.brand,
      data?.model,
      data?.deviceId,
      data?.calibrations,
    ],
  );

  const openListeningDialog = (owner) => {
    if (!owner?.ownerCalibrationId) {
      return;
    }

    setSelectedListeningOwner(owner);
    setListeningDialogOpen(true);
  };

  const closeListeningDialog = () => {
    setListeningDialogOpen(false);
    setSelectedListeningOwner(null);
  };

  const handleListeningCreated = () => {
    if (selectedListeningOwner?.ownerCalibrationId === data?.id) {
      setListeningCountState((prev) => prev + 1);
    }

    if (updateData) {
      Promise.resolve(updateData()).catch((error) => {
        console.error("updateData failed after listening create", error);
      });
    }
  };

  const handleListeningDeleted = () => {
    if (selectedListeningOwner?.ownerCalibrationId === data?.id) {
      setListeningCountState((prev) => Math.max(0, prev - 1));
    }

    if (updateData) {
      Promise.resolve(updateData()).catch((error) => {
        console.error("updateData failed after listening delete", error);
      });
    }
  };

  const averagedNotesCount = averagedNotes.getNotesCount(
    data,
    data?.notes?.size || 0,
  );

  const colSpan =
    1 +
    (config.mainRowCells?.length || 0) +
    displayedFrequencies.length +
    1 +
    (isModelView ? 1 : 0) +
    (isDetailedData && !isModelView ? 1 : 0);

  const handleCheckboxChange = async (componentId) => {
    const isCurrentlyChecked = checkBoxes[componentId];

    if (isCurrentlyChecked) {
      await excludeCheckBox(componentId);
    } else {
      await includeCheckBox(componentId);
    }

    setCheckBoxes((prevCheckBoxes) => ({
      ...prevCheckBoxes,
      [componentId]: !prevCheckBoxes[componentId],
    }));

    if (updateData) {
      await updateData();
    }
  };

  const renderProps = {
    mainCalibrations,
    removeGroupHandler,
    checkBoxes,
    handleCheckboxChange,
    isDetailedData,
    handleOpenListenings: openListeningDialog,
    handleOpenRawNotes: rawNotes.openNotesDialog,
    getRawNotesCount: rawNotes.getNotesCount,
  };

  const findCalibration = (calibrations, freq) => {
    if (!Array.isArray(calibrations) || calibrations.length === 0) {
      return null;
    }

    return (
      calibrations.find((item) => Number(item?.freq) === Number(freq)) || null
    );
  };

  const formatCompactCalibration = (calibration) => {
    const maxIntensity = calibration?.maxIntensity;
    if (maxIntensity == null || isNaN(maxIntensity)) {
      return "Brak Danych";
    }

    return `${Math.round(maxIntensity * 10) / 10} dB`;
  };

  const renderAdvancedModelCalibration = (calibration, frequency) => {
    const microphoneCalibration = resolveMicrophoneCalibration(data);
    const microphoneSignalPower = getMicrophoneSignalPower(
      microphoneCalibration,
      frequency,
    );

    return (
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Box
          display="flex"
          alignItems="center"
          title="Głośność aplikacji"
          aria-label="Głośność aplikacji"
          mb={0.5}
        >
          <VolumeUpIcon fontSize="small" style={{ marginRight: 6 }} />
          <span>{formatAppVolume(calibration?.appVolume)}</span>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          title="Siła sygnału mikrofonu"
          aria-label="Siła sygnału mikrofonu"
        >
          <MicIcon fontSize="small" style={{ marginRight: 6 }} />
          <span>{formatMicrophoneSignalPower(microphoneSignalPower)}</span>
        </Box>
      </Box>
    );
  };

  const renderParentCalibrationCell = (freq) => {
    const calibration = findCalibration(data?.calibrations, freq);

    return (
      <TableCell key={freq}>
        {isDetailedData
          ? isModelView
            ? renderAdvancedModelCalibration(calibration, freq)
            : formatAppVolume(calibration?.appVolume)
          : formatCompactCalibration(calibration)}
      </TableCell>
    );
  };

  const renderExpandedComponentRow = (component, index) =>
    config.collapsibleRowRender(component, index, renderProps);

  const collapsibleHeaders = config.getCollapsibleHeaders
    ? config.getCollapsibleHeaders({ isDetailedData })
    : isDetailedData
    ? config.detailedCollapsibleHeaders || config.collapsibleHeaders
    : config.collapsibleHeaders;

  return (
    <>
      <TableRow
        style={{
          backgroundColor: "#f5f5f5",
        }}
      >
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {config.mainRowCells.map((renderCell) => renderCell(data))}

        {isDetailedData && !isModelView && (
          <TableCell aria-hidden="true" />
        )}

        {displayedFrequencies.map(renderParentCalibrationCell)}

        <ActionCountCell
          label="Notatki"
          icon={NoteIcon}
          onClick={() => averagedNotes.openNotesDialog(data)}
          count={averagedNotesCount}
        />

        {isModelView && (
          <ActionCountCell
            label="Odsłuchy"
            icon={HearingIcon}
            onClick={() => openListeningDialog(topLevelListeningOwner)}
            count={listeningCountState}
            disabled={!topLevelListeningOwner?.ownerCalibrationId}
          />
        )}
      </TableRow>

      <AveragedExpandedRowContent
        open={open}
        colSpan={colSpan}
        headers={collapsibleHeaders}
        columnWidths={config.getColumnWidths({ isDetailedData })}
        components={data?.components || []}
        renderRow={renderExpandedComponentRow}
      />

      <ListeningDialog
        open={listeningDialogOpen}
        onClose={closeListeningDialog}
        ownerCalibrationId={selectedListeningOwner?.ownerCalibrationId}
        averagingLevel={selectedListeningOwner?.averagingLevel || null}
        defaultDeviceId={selectedListeningOwner?.deviceId || ""}
        defaultModelName={selectedListeningOwner?.model || data?.model || ""}
        defaultBrandName={selectedListeningOwner?.brand || data?.brand || ""}
        availableFrequencies={
          selectedListeningOwner?.availableFrequencies || []
        }
        onListeningCreated={handleListeningCreated}
        onListeningDeleted={handleListeningDeleted}
      />

      <CalibrationNotesDialog
        open={averagedNotes.open}
        onClose={averagedNotes.closeNotesDialog}
        title="Notatki uśrednionej kalibracji"
        notes={averagedNotes.notes}
        loading={averagedNotes.loadingNotes}
        error={averagedNotes.notesError}
        editingNoteId={averagedNotes.editingNoteId}
        editingText={averagedNotes.editingText}
        onEditingTextChange={averagedNotes.setEditingText}
        saving={averagedNotes.saving}
        deletingId={averagedNotes.deletingId}
        newNoteText={averagedNotes.newNoteText}
        onNewNoteTextChange={averagedNotes.setNewNoteText}
        adding={averagedNotes.adding}
        onStartEdit={averagedNotes.startEdit}
        onCancelEdit={averagedNotes.cancelEdit}
        onSaveEdit={averagedNotes.saveEdit}
        onDeleteNote={averagedNotes.handleDeleteNote}
        onStartAdd={averagedNotes.startAdd}
        onCancelAdd={averagedNotes.cancelAdd}
        onSaveNew={averagedNotes.saveNew}
        emptyText="Brak notatek."
      />

      <CalibrationNotesDialog
        open={rawNotes.open}
        onClose={rawNotes.closeNotesDialog}
        title="Notatki kalibracji"
        notes={rawNotes.notes}
        loading={rawNotes.loadingNotes}
        error={rawNotes.notesError}
        editingNoteId={rawNotes.editingNoteId}
        editingText={rawNotes.editingText}
        onEditingTextChange={rawNotes.setEditingText}
        saving={rawNotes.saving}
        deletingId={rawNotes.deletingId}
        newNoteText={rawNotes.newNoteText}
        onNewNoteTextChange={rawNotes.setNewNoteText}
        adding={rawNotes.adding}
        onStartEdit={rawNotes.startEdit}
        onCancelEdit={rawNotes.cancelEdit}
        onSaveEdit={rawNotes.saveEdit}
        onDeleteNote={rawNotes.handleDeleteNote}
        onStartAdd={rawNotes.startAdd}
        onCancelAdd={rawNotes.cancelAdd}
        onSaveNew={rawNotes.saveNew}
        emptyText="Brak notatek."
      />
    </>
  );
}
